
import React from "react";
import { StartupAccount } from "@/types/startup";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { CheckCircle, XCircle, Globe, Calendar, Building2 } from "lucide-react";

interface StartupApplicationDetailsProps {
  application: StartupAccount;
  onClose: () => void;
  onUpdateStatus: (applicationId: string, status: 'approved' | 'rejected') => void;
}

const StartupApplicationDetails = ({ 
  application, 
  onClose, 
  onUpdateStatus 
}: StartupApplicationDetailsProps) => {
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Building2 className="h-5 w-5" />
            {application.startup_name}
          </DialogTitle>
          <DialogDescription>
            Application submitted on {new Date(application.submitted_at).toLocaleDateString()}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {/* Basic Information */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Application Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <h4 className="font-medium mb-2">About the Startup</h4>
                <p className="text-sm text-muted-foreground">
                  {application.bio || 'No description provided'}
                </p>
              </div>

              {application.website_url && (
                <div>
                  <h4 className="font-medium mb-2 flex items-center gap-2">
                    <Globe className="h-4 w-4" />
                    Website
                  </h4>
                  <a 
                    href={application.website_url} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:underline text-sm"
                  >
                    {application.website_url}
                  </a>
                </div>
              )}

              {application.category_tags && application.category_tags.length > 0 && (
                <div>
                  <h4 className="font-medium mb-2">Categories</h4>
                  <div className="flex flex-wrap gap-2">
                    {application.category_tags.map((tag) => (
                      <Badge key={tag} variant="secondary">
                        {tag}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}

              <div className="grid grid-cols-2 gap-4 pt-4 border-t">
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Calendar className="h-4 w-4" />
                  Submitted: {new Date(application.submitted_at).toLocaleDateString()}
                </div>
                <div className="flex items-center gap-2 text-sm">
                  Status: 
                  <Badge 
                    variant={
                      application.status === 'approved' ? 'default' : 
                      application.status === 'rejected' ? 'destructive' : 
                      'secondary'
                    }
                  >
                    {application.status}
                  </Badge>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Actions */}
          {application.status === 'pending' && (
            <div className="flex gap-3">
              <Button
                className="flex-1"
                onClick={() => onUpdateStatus(application.id, 'approved')}
              >
                <CheckCircle className="h-4 w-4 mr-2" />
                Approve Application
              </Button>
              <Button
                variant="destructive"
                className="flex-1"
                onClick={() => onUpdateStatus(application.id, 'rejected')}
              >
                <XCircle className="h-4 w-4 mr-2" />
                Reject Application
              </Button>
            </div>
          )}

          {application.status !== 'pending' && (
            <div className="text-center py-4 text-sm text-muted-foreground">
              This application has been {application.status}
              {application.approved_at && (
                <span> on {new Date(application.approved_at).toLocaleDateString()}</span>
              )}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default StartupApplicationDetails;
